/**
 * Organization Module Service
 *
 * Owns the org-scoped module collection (`org/{orgId}/modules`): adding,
 * removing, and overriding the modules enabled for an organization.
 *
 * Pure TypeScript (no React / no UI). Shared as-is by web (console) and mobile (RN).
 * Menu tree is resolved at runtime from module-groups + grants (spec 0038) — no
 * derived flatten to recompute (0150).
 */
import { repositoryFactory, DataSourceStrategy, CacheStrategy } from '@rammetec/data-access';
import { rtLogService } from '@rammetec/core';
import { ORGANIZATION_MODULE } from '../types/module.js';
import { ORGANIZATION_DATA } from '../types/organization.js';
import { moduleService } from './moduleService.js';
import { moduleGroupService } from './moduleGroupService.js';
import { planService } from './planService.js';
import { entitledModuleIds } from '../utils/moduleResolution.js';
import { lifecycleService } from './lifecycleService.js';
import { normalizeTag } from '../utils/moduleTags.js';
class OrganizationModuleService {
    getRepo(orgId) {
        return repositoryFactory.getRepository(`org/${orgId}/modules`, ORGANIZATION_MODULE, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
    }
    /** All modules enabled for the organization. */
    async getOrgModules(orgId) {
        const repo = await this.getRepo(orgId);
        return repo.getAll();
    }
    /**
     * Whether the org has contracted the module whose `actionProperties.redirectTo`
     * equals `slug` (case-insensitive, matching the console shell).
     * Uses existing reads only — no raw query. Request 0269 / spec 0048 A2.
     */
    async hasModule(orgId, slug) {
        const needle = slug.toLowerCase();
        if (!needle)
            return false;
        const [orgModules, catalog] = await Promise.all([
            this.getOrgModules(orgId),
            moduleService.getAllModules(),
        ]);
        const matchingBaseIds = new Set();
        for (const mod of catalog) {
            const redirectTo = mod.actionProperties?.redirectTo;
            if (typeof redirectTo === 'string' && redirectTo.toLowerCase() === needle) {
                matchingBaseIds.add(mod.id);
            }
        }
        if (matchingBaseIds.size === 0)
            return false;
        return orgModules.some((om) => matchingBaseIds.has(om.moduleId));
    }
    /** Enables a base module for the org (optional group/order). */
    async addModuleToOrg(orgId, moduleId, opts) {
        const repo = await this.getRepo(orgId);
        return repo.create({
            moduleId,
            ...(opts?.groupId ? { groupId: opts.groupId } : {}),
            ...(opts?.order != null ? { order: opts.order } : {}),
        });
    }
    /** Removes an org-module entry. */
    async removeModuleFromOrg(orgId, orgModuleId) {
        const repo = await this.getRepo(orgId);
        await repo.delete(orgModuleId);
    }
    /** Applies per-org overrides to an org-module entry. */
    async updateOrgModule(orgId, orgModuleId, delta) {
        const repo = await this.getRepo(orgId);
        await repo.update(orgModuleId, delta);
    }
    /**
     * Creates OrganizationModule rows for every entitled base module that has no
     * line yet. Never deletes. Places by first ModuleGroup whose autoTags intersect
     * the module tags; otherwise root. Request 0149 §4.
     *
     * Org with no active plan → orphans reported as [] (nothing to compare against).
     */
    async syncPlans(orgId) {
        const [orgRepo, catalog, existing, groups, allPlans] = await Promise.all([
            repositoryFactory.getRepository('org', ORGANIZATION_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory),
            moduleService.getAllModules(),
            this.getOrgModules(orgId),
            moduleGroupService.getGroups(orgId),
            planService.getAllPlans(),
        ]);
        const org = await orgRepo.getById(orgId);
        const planIds = new Set(org?.planIds ?? []);
        const activePlans = allPlans.filter((p) => planIds.has(p.id) && lifecycleService.isActive(p));
        if (activePlans.length === 0) {
            return { created: [], orphans: [], missingCount: 0 };
        }
        const { missing, orphans } = entitledModuleIds(activePlans, catalog, existing);
        const catalogById = new Map(catalog.map((m) => [m.id, m]));
        const created = [];
        // 0152: never materialize catalog rows with action:'group' (legacy group modules).
        const toCreate = [];
        for (const baseId of missing) {
            const base = catalogById.get(baseId);
            if (base?.action === 'group') {
                rtLogService.warn('[organizationModuleService] syncPlans skipped action:group base module', { orgId, baseId, name: base.name });
                continue;
            }
            toCreate.push(baseId);
        }
        for (const baseId of toCreate) {
            const base = catalogById.get(baseId);
            const groupId = this.pickGroupByAutoTags(base, groups);
            const om = await this.addModuleToOrg(orgId, baseId, groupId ? { groupId } : undefined);
            created.push(om);
        }
        return { created, orphans, missingCount: toCreate.length };
    }
    /**
     * First group whose autoTags intersect the module tags (facilitador 2).
     * Undefined → root.
     */
    pickGroupByAutoTags(mod, groups) {
        if (!mod?.tags?.length)
            return undefined;
        const modTags = new Set(mod.tags.map((t) => normalizeTag(t)).filter(Boolean));
        for (const g of groups) {
            const auto = (g.autoTags ?? []).map((t) => normalizeTag(t)).filter(Boolean);
            if (auto.some((t) => modTags.has(t)))
                return g.id;
        }
        return undefined;
    }
    /**
     * Facilitador 3 / 0149 §5 — distribute a catalog module to many orgs.
     * Reuses addModuleToOrg placement (autoTags). Never deletes. Skips orgs that
     * already have the base module (unless allowMultiple).
     */
    async distributeModuleToOrgs(moduleId, orgIds) {
        const catalog = await moduleService.getAllModules();
        const base = catalog.find((m) => m.id === moduleId);
        if (!base) {
            throw new Error(`Module ${moduleId} not found in catalog`);
        }
        const created = [];
        const skipped = [];
        const failed = [];
        for (const orgId of orgIds) {
            try {
                const [existing, groups] = await Promise.all([
                    this.getOrgModules(orgId),
                    moduleGroupService.getGroups(orgId),
                ]);
                const already = existing.some((om) => om.moduleId === moduleId);
                if (already && !base.allowMultipleInsertions) {
                    skipped.push({ orgId, reason: 'já existe' });
                    continue;
                }
                const groupId = this.pickGroupByAutoTags(base, groups);
                const om = await this.addModuleToOrg(orgId, moduleId, groupId ? { groupId } : undefined);
                created.push({ orgId, orgModuleId: om.id });
            }
            catch (error) {
                failed.push({
                    orgId,
                    error: error instanceof Error ? error.message : String(error),
                });
                rtLogService.warn('[organizationModuleService] distribute failed for org', {
                    orgId,
                    moduleId,
                    error,
                });
            }
        }
        return { created, skipped, failed };
    }
    /** Dry read of entitlement for UI counters — no writes. */
    async previewSyncPlans(orgId) {
        const [orgRepo, catalog, existing, allPlans] = await Promise.all([
            repositoryFactory.getRepository('org', ORGANIZATION_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory),
            moduleService.getAllModules(),
            this.getOrgModules(orgId),
            planService.getAllPlans(),
        ]);
        const org = await orgRepo.getById(orgId);
        const planIds = new Set(org?.planIds ?? []);
        const activePlans = allPlans.filter((p) => planIds.has(p.id) && lifecycleService.isActive(p));
        if (activePlans.length === 0) {
            return { missingCount: 0, orphans: [], hasActivePlans: false };
        }
        const { missing, orphans } = entitledModuleIds(activePlans, catalog, existing);
        return { missingCount: missing.length, orphans, hasActivePlans: true };
    }
}
export const organizationModuleService = new OrganizationModuleService();
//# sourceMappingURL=organizationModuleService.js.map